function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["rotate", "dominantBaseline"];
import * as React from 'react';
import NoSsr from '@mui/material/NoSsr';
import { useTheme, styled } from '@mui/material/styles';
import { ChartsText } from '../ChartsText';
import { getWordsByLines } from '../internals/getWordsByLines';
import { legendItemPlacements } from './legendItemsPlacement';
import { useDrawingArea } from '../hooks/useDrawingArea';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const ChartsLegendRoot = styled('g', {
  name: 'MuiChartsLegend',
  slot: 'Root',
  overridesResolver: (props, styles) => styles.root
})({});
/**
 * Transforms number or partial padding object to a defaultized padding object.
 */
const getStandardizedPadding = padding => {
  if (typeof padding === 'number') {
    return {
      left: padding,
      right: padding,
      top: padding,
      bottom: padding
    };
  }
  return _extends({
    left: 0,
    right: 0,
    top: 0,
    bottom: 0
  }, padding);
};

/**
 * Internal component to display an array of items as a legend.
 * Used for series legend, and threshold color legend.
 * @ignore - Do not document
 */
export function LegendPerItem(props) {
  const hidden = props.hidden,
    position = props.position,
    direction = props.direction,
    itemsToDisplay = props.itemsToDisplay,
    classes = props.classes,
    _props$itemMarkWidth = props.itemMarkWidth,
    itemMarkWidth = _props$itemMarkWidth === void 0 ? 20 : _props$itemMarkWidth,
    _props$itemMarkHeight = props.itemMarkHeight,
    itemMarkHeight = _props$itemMarkHeight === void 0 ? 20 : _props$itemMarkHeight,
    _props$markGap = props.markGap,
    markGap = _props$markGap === void 0 ? 5 : _props$markGap,
    _props$itemGap = props.itemGap,
    itemGap = _props$itemGap === void 0 ? 10 : _props$itemGap,
    _props$padding = props.padding,
    paddingProps = _props$padding === void 0 ? 10 : _props$padding,
    inLabelStyle = props.labelStyle;
  const theme = useTheme();
  const isRTL = theme.direction === 'rtl';
  const drawingArea = useDrawingArea();
  const labelStyle = React.useMemo(() => _extends({}, theme.typography.subtitle1, {
    color: 'inherit',
    dominantBaseline: 'central',
    textAnchor: 'start',
    fill: (theme.vars || theme).palette.text.primary,
    lineHeight: 1
  }, inLabelStyle),
  // To say to TS that the dominantBaseline and textAnchor are correct
  [inLabelStyle, theme]);
  const padding = React.useMemo(() => getStandardizedPadding(paddingProps), [paddingProps]);
  const getItemSpace = React.useCallback(function (label) {
    let inStyle = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const style = _objectWithoutPropertiesLoose(inStyle, _excluded);
    const linesSize = getWordsByLines({
      style,
      needsComputation: true,
      text: label
    });
    const innerSize = {
      innerWidth: itemMarkWidth + markGap + Math.max(...linesSize.map(size => size.width)),
      innerHeight: Math.max(itemMarkHeight, linesSize.length * linesSize[0].height)
    };
    return _extends({}, innerSize, {
      outerWidth: innerSize.innerWidth + itemGap,
      outerHeight: innerSize.innerHeight + itemGap
    });
  }, [itemGap, itemMarkHeight, itemMarkWidth, markGap]);
  const totalWidth = drawingArea.left + drawingArea.width + drawingArea.right;
  const totalHeight = drawingArea.top + drawingArea.height + drawingArea.bottom;
  const availableWidth = totalWidth - padding.left - padding.right;
  const availableHeight = totalHeight - padding.top - padding.bottom;
  const _React$useMemo = React.useMemo(() => legendItemPlacements(itemsToDisplay, getItemSpace, labelStyle, direction, availableWidth, availableHeight, itemGap), [itemsToDisplay, getItemSpace, labelStyle, direction, availableWidth, availableHeight, itemGap]),
    _React$useMemo2 = _slicedToArray(_React$useMemo, 3),
    itemsWithPosition = _React$useMemo2[0],
    legendWidth = _React$useMemo2[1],
    legendHeight = _React$useMemo2[2];
  const gapX = React.useMemo(() => {
    switch (position.horizontal) {
      case 'left':
        return padding.left;
      case 'right':
        return totalWidth - padding.right - legendWidth;
      default:
        return (totalWidth - legendWidth) / 2;
    }
  }, [position.horizontal, padding.left, padding.right, totalWidth, legendWidth]);
  const gapY = React.useMemo(() => {
    switch (position.vertical) {
      case 'top':
        return padding.top;
      case 'bottom':
        return totalHeight - padding.bottom - legendHeight;
      default:
        return (totalHeight - legendHeight) / 2;
    }
  }, [position.vertical, padding.top, padding.bottom, totalHeight, legendHeight]);
  if (hidden) {
    return null;
  }
  return /*#__PURE__*/_jsx(NoSsr, {
    children: /*#__PURE__*/_jsx(ChartsLegendRoot, {
      className: classes === null || classes === void 0 ? void 0 : classes.root,
      children: itemsWithPosition.map(_ref => {
        let id = _ref.id,
          label = _ref.label,
          color = _ref.color,
          positionX = _ref.positionX,
          positionY = _ref.positionY;
        return /*#__PURE__*/_jsxs("g", {
          className: classes === null || classes === void 0 ? void 0 : classes.series,
          transform: `translate(${gapX + (isRTL ? legendWidth - positionX : positionX)} ${gapY + positionY})`,
          children: [/*#__PURE__*/_jsx("rect", {
            className: classes === null || classes === void 0 ? void 0 : classes.mark,
            x: isRTL ? -itemMarkWidth : 0,
            y: -itemMarkHeight / 2,
            width: itemMarkWidth,
            height: itemMarkHeight,
            fill: color
          }), /*#__PURE__*/_jsx(ChartsText, {
            style: labelStyle,
            text: label,
            x: (isRTL ? -1 : 1) * (itemMarkWidth + markGap),
            y: 0
          })]
        }, id);
      })
    })
  });
}